import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {Card, Form, List} from 'antd';
import styles from '@/modules/templates/components/TemplateDetailsInSituMeasure/index.less';
import TemplateDetailsFooterActions from '@/modules/templates/components/TemplateDetailsFooterActions';
import InSituMeasureListItem from '@/modules/templates/components/TemplateDetailsInSituMeasure/InSituMeasureListItem';
import InSituMeasureEditableProvider
from '@/modules/templates/components/TemplateDetailsInSituMeasure/InSituMeasureEditableProvider';
import InSituMeasurePlaceSelect from '@/modules/templates/components/TemplateDetailsInSituMeasure/InSituMeasurePlaceSelector';
import {useTranslation} from 'react-i18next';

const InSituMeasureMobileView = ({question, sectionId, runAudit}) => {
  const {t}  = useTranslation('templates');
  const [dataSource, setDataSource] = useState([]);
  const [selectedPlace, setSelectedPlace] = useState(null);

  const readingRegistryResponses = useMemo(() => {
    return question?.answer?.templateAnswer?.readingRegistry || [];
  }, [question?.answer?.templateAnswer]);

  const placesMeasure = useCallback(() => {
    return question?.readingRegistry?.placesMeasure?.map((place, index) => ({...place, keyPos: [index]})) || [];
  }, [question?.readingRegistry]);

  const hasEmptyPlace = useCallback(() => (
    placesMeasure()?.findIndex(place => !place?.place) !== -1
  ),[placesMeasure]);

  const nextPlace = useCallback(() => {
    const allPlaces = placesMeasure();
    let next = placesMeasure()?.findIndex(place => !place?.place);
    if(next < 0)
      next = selectedPlace?.keyPos?.[0] + 1;

    setSelectedPlace(allPlaces?.[next]);
  }, [placesMeasure, selectedPlace?.keyPos]);

  useEffect(() => {
    const place = placesMeasure()?.[0];
    if(place)
      setSelectedPlace(place);

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const measures = readingRegistryResponses?.filter?.(r => r?.placesMeasure === selectedPlace?._id);

    const data = question?.readingRegistry?.options?.map(item => {
      let result;
      let record;
      const dataIndex = item?.key;
      if(item?.key === 'place') {
        record = {dataIndex, ...item, ...selectedPlace};
        result = ({...item, ...selectedPlace, placeholder: t('placeNumber', {number: selectedPlace?.keyPos?.[0] + 1 || ''})});
      } else {
        const data = measures?.find?.(m => m?.optionKey === item?.key);
        record = {dataIndex, ...selectedPlace, [dataIndex]: data?.value};
        result = ({...item, title: item?.label, ...data});
      }
      return {...result, dataIndex, question, sectionId, runAudit, record};
    });
    setDataSource(data);
  }, [selectedPlace, question?.readingRegistry?.options, readingRegistryResponses, question, sectionId, runAudit, t]);

  const handleSave = useCallback((value) => {
    const measureValue = value?.[value?.dataIndex];
    if(value?.dataIndex === 'place') {
      setSelectedPlace(prev => ({...prev, label: measureValue, place: measureValue}));
    }
    setDataSource(prev => {
      return prev?.map(item => {
        if (item?.dataIndex === value?.dataIndex) {
          return {...item, value: measureValue, record: {...item?.record, [item?.dataIndex]: measureValue}};
        }
        return item;
      });
    });
  }, []);

  return (
    <>
      <Card
        className={styles.templateTextCard}
        id={'question-view-'+question?._id}
      >
        <div className="mb-3">
          {question?.name || question?.label}
        </div>

        <>
          <InSituMeasurePlaceSelect places={placesMeasure()} selectedPlace={selectedPlace} onChange={setSelectedPlace}/>
        </>
        <Form layout="vertical">
          <Form.Item name="nameInSituMeasure">
            <List dataSource={dataSource} renderItem={(item, index) =>
              <InSituMeasureEditableProvider key={item?.key} >
                <InSituMeasureListItem
                  measure={item}
                  handleSave={handleSave}
                  nextPlace={nextPlace}
                  hasEmptyPlace={hasEmptyPlace()}
                  style={{backgroundColor: index % 2 === 0 ? undefined : '#f5f5f5'}}
                />
              </InSituMeasureEditableProvider>
            }
            />
          </Form.Item>
        </Form>

        <TemplateDetailsFooterActions question={question} sectionId={sectionId} runAudit={runAudit} />
      </Card>
    </>
  );
};

InSituMeasureMobileView.propTypes = {
  question: PropTypes.object,
  runAudit: PropTypes.bool,
  sectionId: PropTypes.string,
};

export default memo(InSituMeasureMobileView);
